import { useEffect, useState } from "react";
import type {
  ForecastRuleCell,
  ForecastScenario,
  RecurrenceType,
} from "../../hooks/useForecastCategoryRules";

type Props = {
  isOpen: boolean;
  categoryName: string;
  scenario: ForecastScenario;
  initialCell: ForecastRuleCell | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: {
    recurrenceType: RecurrenceType;
    lookbackMonths?: number | null;
    yearlyGrowthRate?: number | null;
    isActive?: boolean;
  }) => Promise<void>;
  onClear: () => Promise<void>;
};

const scenarioLabel: Record<ForecastScenario, string> = {
  bad: "Bad",
  mid: "Mid",
  good: "Good",
};

export function ForecastRuleEditorModal({
  isOpen,
  categoryName,
  scenario,
  initialCell,
  isSaving,
  onClose,
  onSave,
  onClear,
}: Props) {
  const defaultType: RecurrenceType = initialCell?.recurrenceType || "average_last_x_months";
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(defaultType);
  const [lookbackMonths, setLookbackMonths] = useState<number>(
    initialCell?.lookbackMonths || 6,
  );
  const [yearlyGrowthPercent, setYearlyGrowthPercent] = useState<number>(
    Math.round((initialCell?.yearlyGrowthRate || 0) * 100),
  );
  const [isActive, setIsActive] = useState(initialCell?.isActive ?? true);

  useEffect(() => {
    if (!isOpen) return;
    setRecurrenceType(initialCell?.recurrenceType || "average_last_x_months");
    setLookbackMonths(initialCell?.lookbackMonths || 6);
    setYearlyGrowthPercent(Math.round((initialCell?.yearlyGrowthRate || 0) * 100));
    setIsActive(initialCell?.isActive ?? true);
  }, [initialCell, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-slate-900">
            {categoryName} - {scenarioLabel[scenario]} scenario
          </h3>
          <p className="mt-1 text-sm text-slate-500">Set rule type and parameters.</p>
        </div>

        <div className="space-y-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-500">Rule type</span>
            <select
              value={recurrenceType}
              onChange={(e) => setRecurrenceType(e.target.value as RecurrenceType)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="average_last_x_months">Average of last X months</option>
              <option value="yearly_percentage_growth">Yearly percentage growth</option>
              <option value="copy_last_month">Copy from last month</option>
            </select>
          </label>

          {recurrenceType === "average_last_x_months" ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-500">Months (X)</span>
              <input
                type="number"
                min={1}
                max={36}
                value={lookbackMonths}
                onChange={(e) => setLookbackMonths(Number(e.target.value || 6))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              />
            </label>
          ) : null}

          {recurrenceType === "yearly_percentage_growth" ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-500">Yearly growth (%)</span>
              <input
                type="number"
                step="1"
                value={yearlyGrowthPercent}
                onChange={(e) => setYearlyGrowthPercent(Number(e.target.value || 0))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              />
            </label>
          ) : null}

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Rule active
          </label>
        </div>

        <div className="mt-5 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => {
              void onClear();
            }}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
            disabled={isSaving}
          >
            Reset to default
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                const payload =
                  recurrenceType === "average_last_x_months"
                    ? {
                        recurrenceType,
                        lookbackMonths,
                        yearlyGrowthRate: null,
                        isActive,
                      }
                    : recurrenceType === "yearly_percentage_growth"
                      ? {
                          recurrenceType,
                          lookbackMonths: null,
                          yearlyGrowthRate: yearlyGrowthPercent / 100,
                          isActive,
                        }
                      : {
                          recurrenceType,
                          lookbackMonths: null,
                          yearlyGrowthRate: null,
                          isActive,
                        };
                void onSave(payload);
              }}
              className="rounded-lg bg-[#4B7F73] px-3 py-2 text-sm font-semibold text-white hover:bg-[#3F6C61] disabled:opacity-60"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
